import ListItem from "@/features/list/components/ListItem";
import { MakerArchiveItem } from "@/features/maker/components/MakerArchiveItem";
import PostItem from "@/features/post/components/PostItem";
import PostItemList from "@/features/post/components/PostItemList";
import ProductVariationItem from "@/features/product_variation/components/ProductVariationItem";
import ProjectItem from "@/features/project/components/ProjectItem";
import SearchResultSwiper from "@/pages/SearchIndexPage/components/SearchResultSwiper";
import { MutateItems } from "@/pages/SearchIndexPage/hooks/useSearchIndex";
import { SearchResult, SectionId } from "@/pages/SearchIndexPage/types";
import { formatSearchCount } from "@/utils/formatSearchCount";
import {
  BoardResponse,
  ListResponse,
  MakerResponse,
  PostResponse,
  ProductVariationWithProductResponse,
  ProjectResponse,
  TeamResponse,
} from "@/web-client/api";
import Link from "next/link";
import { FC, PropsWithChildren } from "react";
import { SwiperSlide } from "swiper/react";
import BoardItem from "@/features/board/components/BoardItem";
import TeamItem from "@/features/team/components/TeamItem";
import ModalEntityListWrapper from "@/components/ModalEntityListWrapper";

type Props = {
  keyword: string;
  result: SearchResult;
  mutateItems: MutateItems;
};

export const SearchResultSection: FC<Props> = ({
  result,
  keyword,
  mutateItems,
}) => {
  const { section_id } = result;

  if (section_id === SectionId.Team)
    return (
      <SearchResultSectionLayout
        result={result}
        keyword={keyword}
        href={`/search/teams?keyword=${keyword}`}
      >
        <SearchResultSwiper>
          {result.items.map((item) => (
            <SwiperSlide
              className="!w-[24%] min-w-[240px] max-w-[320px]"
              key={item.id}
            >
              <TeamItem team={item as TeamResponse} />
            </SwiperSlide>
          ))}
        </SearchResultSwiper>
      </SearchResultSectionLayout>
    );

  if (section_id === SectionId.Maker)
    return (
      <SearchResultSectionLayout
        result={result}
        keyword={keyword}
        href={`/search/makers?keyword=${keyword}`}
      >
        <SearchResultSwiper>
          {result.items.map((item) => (
            <SwiperSlide
              className="!w-[24%] min-w-[240px] max-w-[320px]"
              key={item.id}
            >
              <MakerArchiveItem maker={item as MakerResponse} />
            </SwiperSlide>
          ))}
        </SearchResultSwiper>
      </SearchResultSectionLayout>
    );

  if (section_id === SectionId.List)
    return (
      <SearchResultSectionLayout
        result={result}
        keyword={keyword}
        href={`/search/lists?keyword=${keyword}`}
      >
        <SearchResultSwiper>
          {result.items.map((item) => (
            <SwiperSlide
              className="!w-[24%] min-w-[240px] max-w-[320px]"
              key={item.id}
            >
              <ListItem list={item as ListResponse} />
            </SwiperSlide>
          ))}
        </SearchResultSwiper>
      </SearchResultSectionLayout>
    );

  if (section_id === SectionId.Board)
    return (
      <SearchResultSectionLayout
        result={result}
        keyword={keyword}
        href={`/search/boards?keyword=${keyword}`}
      >
        <SearchResultSwiper>
          {result.items.map((item) => (
            <SwiperSlide
              className="!w-[24%] min-w-[240px] max-w-[320px]"
              key={item.id}
            >
              <BoardItem board={item as BoardResponse} />
            </SwiperSlide>
          ))}
        </SearchResultSwiper>
      </SearchResultSectionLayout>
    );

  if (section_id === SectionId.Interior)
    return (
      <SearchResultSectionLayout
        result={result}
        keyword={keyword}
        href={`/search/interiors?keyword=${keyword}`}
      >
        <ModalEntityListWrapper
          responses={result.items as ProductVariationWithProductResponse[]}
        >
          <SearchResultSwiper>
            {result.items.map((item) => (
              <SwiperSlide
                className="!w-[24%] min-w-[240px] max-w-[320px]"
                key={item.id}
              >
                <ProductVariationItem
                  productVariation={item as ProductVariationWithProductResponse}
                  mutate={(updatedData) => {
                    mutateItems("interiors", updatedData);
                  }}
                />
              </SwiperSlide>
            ))}
          </SearchResultSwiper>
        </ModalEntityListWrapper>
      </SearchResultSectionLayout>
    );

  if (section_id === SectionId.BuildingMaterial)
    return (
      <SearchResultSectionLayout
        result={result}
        keyword={keyword}
        href={`/search/building_materials?keyword=${keyword}`}
      >
        <ModalEntityListWrapper
          responses={result.items as ProductVariationWithProductResponse[]}
        >
          <SearchResultSwiper>
            {result.items.map((item) => (
              <SwiperSlide
                className="min-w-[160px] max-w-[200px]"
                key={item.id}
              >
                <ProductVariationItem
                  productVariation={item as ProductVariationWithProductResponse}
                  mutate={(updatedData) => {
                    mutateItems("building_materials", updatedData);
                  }}
                />
              </SwiperSlide>
            ))}
          </SearchResultSwiper>
        </ModalEntityListWrapper>
      </SearchResultSectionLayout>
    );

  if (section_id === SectionId.Project)
    return (
      <SearchResultSectionLayout
        result={result}
        keyword={keyword}
        href={`/search/projects?keyword=${keyword}`}
      >
        <ModalEntityListWrapper responses={result.items as ProjectResponse[]}>
          <SearchResultSwiper>
            {result.items.map((item) => (
              <SwiperSlide
                className="!w-[24%] min-w-[240px] max-w-[320px]"
                key={item.id}
              >
                <ProjectItem
                  project={item as ProjectResponse}
                  mutate={(updatedData) => {
                    mutateItems("projects", updatedData);
                  }}
                />
              </SwiperSlide>
            ))}
          </SearchResultSwiper>
        </ModalEntityListWrapper>
      </SearchResultSectionLayout>
    );

  if (section_id === SectionId.Post)
    return (
      <SearchResultSectionLayout
        result={result}
        keyword={keyword}
        href={`/search/posts?keyword=${keyword}`}
      >
        <PostItemList
          posts={result.items as PostResponse[]}
          onRender={(post) => (
            <PostItem
              key={post.id}
              post={post}
              mutate={(updated: PostResponse) => {
                mutateItems("posts", updated);
              }}
            />
          )}
        />
      </SearchResultSectionLayout>
    );
};

type SearchResultSectionLayoutProps = PropsWithChildren<{
  result: SearchResult;
  keyword: string;
  href: string;
}>;

const SearchResultSectionLayout: FC<SearchResultSectionLayoutProps> = ({
  result: { count, label, section_id, items },
  keyword,
  href,
  children,
}) => {
  return (
    <section id={section_id} className="space-y-12">
      <div className="flex justify-between items-center">
        <h2 className="font-bold text-lg">
          {label}({formatSearchCount(count)})
        </h2>

        {items.length < count && (
          <Link
            className="text-link text-sm"
            href={href}
            aria-label={`"${keyword}"で検索したときの、${label}のすべての検索結果`}
          >
            すべてを見る
          </Link>
        )}
      </div>

      <div>{children}</div>
    </section>
  );
};
